import React from "react";
import "./App.css";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandingProvider } from "@/lib/branding";
import { AiJobsProvider } from "@/lib/ai-jobs";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AppShell from "@/components/AppShell";
import ProtectedRoute from "@/components/ProtectedRoute";
import AiJobsTray from "@/components/AiJobsTray";

import Landing from "@/pages/Landing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Policy from "@/pages/Policy";
import ProgramDetail from "@/pages/ProgramDetail";
import RoleLoginPage from "@/pages/auth/RoleLoginPage";
import TwoFactorSetup from "@/pages/auth/TwoFactorSetup";
import TwoFactorVerify from "@/pages/auth/TwoFactorVerify";
import Register from "@/pages/Register";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Certificate from "@/pages/Certificate";
import VerifyCertificate from "@/pages/Verify";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import StudentDashboard from "@/pages/student/Dashboard";
import Learn from "@/pages/student/Learn";
import MyAttendance from "@/pages/student/Attendance";
import AccountProfile from "@/pages/AccountProfile";

import InstructorDashboard from "@/pages/instructor/Dashboard";
import InstructorProgramEditor from "@/pages/instructor/ProgramEditor";
import InstructorInbox from "@/pages/instructor/Inbox";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminUsers from "@/pages/admin/Users";
import AdminPrograms from "@/pages/admin/Programs";
import AdminBranding from "@/pages/admin/Branding";
import AdminAttendance from "@/pages/admin/Attendance";
import AdminLoaRegistry from "@/pages/admin/LoaRegistry";
import AdminDeleteRequests from "@/pages/admin/DeleteRequests";
import AdminAuditLog from "@/pages/admin/AuditLog";
import AdminStudents from "@/pages/admin/Students";
import AdminStudentDetail from "@/pages/admin/StudentDetail";
import AdminStudentAttendance from "@/pages/admin/StudentAttendance";
import AdminInstructors from "@/pages/admin/Instructors";
import InstructorStudents from "@/pages/instructor/Students";
import InstructorStudentDetail from "@/pages/instructor/StudentDetail";
import SMEDashboard from "@/pages/sme/Dashboard";

const INTERNAL_PREFIXES = [
    "/dashboard",
    "/learn",
    "/certificate",
    "/me",
    "/account",
    "/instructor",
    "/admin",
];

function RootLayout() {
    const { user } = useAuth();
    const location = useLocation();
    const isInternalRoute = INTERNAL_PREFIXES.some((p) => location.pathname.startsWith(p));
    const showShell = !!user && isInternalRoute;

    if (showShell) {
        return (
            <AppShell>
                <Outlet />
            </AppShell>
        );
    }

    return (
        <>
            <Header />
            <main className="min-h-[60vh]">
                <Outlet />
            </main>
            <Footer />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <BrandingProvider>
                    <AiJobsProvider>
                        <BrowserRouter>
                        <Routes>
                            <Route element={<RootLayout />}>
                                <Route path="/" element={<Landing />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/contact" element={<Contact />} />
                                <Route path="/policies/:slug" element={<Policy />} />
                                <Route path="/programs/:id" element={<ProgramDetail />} />
                                <Route path="/login" element={<RoleLoginPage role="student" />} />
                                {/* Legacy role-specific login URLs — kept as
                                    redirects so old bookmarks/emails don't 404.
                                    Iter34+ accepts any role at /login. */}
                                <Route path="/login/student" element={<Navigate to="/login" replace />} />
                                <Route path="/login/instructor" element={<Navigate to="/login" replace />} />
                                <Route path="/login/sme" element={<Navigate to="/login" replace />} />
                                <Route path="/login/staff" element={<Navigate to="/login" replace />} />
                                <Route path="/login/admin" element={<Navigate to="/login" replace />} />
                                <Route path="/account/2fa/setup" element={<TwoFactorSetup />} />
                                <Route path="/account/2fa/verify" element={<TwoFactorVerify />} />
                                <Route path="/register" element={<Register />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route path="/reset-password/:token" element={<ResetPassword />} />
                                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                                <Route path="/verify/:certId" element={<VerifyCertificate />} />

                                <Route
                                    path="/dashboard"
                                    element={
                                        <ProtectedRoute roles={["student", "instructor", "admin"]}>
                                            <StudentDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/learn/:programId"
                                    element={
                                        <ProtectedRoute roles={["student", "instructor", "admin"]}>
                                            <Learn />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/me/attendance"
                                    element={
                                        <ProtectedRoute roles={["student", "instructor", "admin"]}>
                                            <MyAttendance />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/me/profile"
                                    element={
                                        <ProtectedRoute roles={["student", "instructor", "admin", "sme"]}>
                                            <AccountProfile />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/certificate/:certId"
                                    element={
                                        <ProtectedRoute>
                                            <Certificate />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/account/change-password"
                                    element={
                                        <ProtectedRoute>
                                            <ChangePassword />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/instructor"
                                    element={
                                        <ProtectedRoute roles={["instructor", "admin"]}>
                                            <InstructorDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/instructor/programs/:programId"
                                    element={
                                        <ProtectedRoute roles={["instructor", "admin", "sme"]}>
                                            <InstructorProgramEditor />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/instructor/inbox"
                                    element={
                                        <ProtectedRoute roles={["instructor", "admin"]}>
                                            <InstructorInbox />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/instructor/students"
                                    element={
                                        <ProtectedRoute roles={["instructor", "admin"]}>
                                            <InstructorStudents />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/instructor/students/:studentId"
                                    element={
                                        <ProtectedRoute roles={["instructor", "admin"]}>
                                            <InstructorStudentDetail />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/sme"
                                    element={
                                        <ProtectedRoute roles={["sme", "admin"]}>
                                            <SMEDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/sme/programs/:programId"
                                    element={
                                        <ProtectedRoute roles={["sme", "admin"]}>
                                            <InstructorProgramEditor />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/admin"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/users"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminUsers />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/students"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminStudents />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/students/:studentId"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminStudentDetail />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/students/:studentId/attendance"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminStudentAttendance />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/instructors"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminInstructors />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/programs"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminPrograms />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/branding"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminBranding />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/attendance"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminAttendance />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/loa"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminLoaRegistry />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/delete-requests"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminDeleteRequests />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/audit-log"
                                    element={
                                        <ProtectedRoute roles={["admin"]}>
                                            <AdminAuditLog />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>
                        </Routes>
                        </BrowserRouter>
                        <AiJobsTray />
                    </AiJobsProvider>
                </BrandingProvider>
                <Toaster position="top-right" />
            </AuthProvider>
        </div>
    );
}

export default App;
